import { ChangeEventHandler, FC, MouseEventHandler, ReactNode } from "react";
import "./Table_header.scss";
import { areAccessPermissions, hasPermission } from "../../helpers/helpers";
import { AccessRoles, PermissionRoles } from "../../../models/models";
import { useAppSelector } from "../../hooks/hooks";
import add from "./Img/ADD.svg";
import DownloadButton from "../DownloadButton/DownloadButton";

interface TableHeaderProps {
    title: string;
    onAddRowHandler?: MouseEventHandler<HTMLButtonElement>;
    onUploadFileHandler?: ChangeEventHandler<HTMLInputElement>;
    onUpdateFileHandler?: ChangeEventHandler<HTMLInputElement>;
    onDownloadFileHandler?: () => void;
    uploadAction?: boolean;
    borderRadius?: "small" | "normal";
    children?: ReactNode;
    dataTableLength?: number;
    pageSize?: number;
    headerActions?: ReactNode;
    // Permission required to use the "Add" button. When provided, the button is
    // gated via hasPermission; otherwise it falls back to the admin role check.
    addPermission?: string;
}

const TableHeader: FC<TableHeaderProps> = ({
    title,
    onAddRowHandler,
    onUploadFileHandler,
    onUpdateFileHandler,
    onDownloadFileHandler,
    uploadAction,
    borderRadius,
    children,
    dataTableLength,
    pageSize,
    headerActions,
    addPermission,
}) => {
    const user = useAppSelector((state) => state.initializeApp);
    const isAccessPermission = addPermission
        ? hasPermission(user.permissions, addPermission)
        : areAccessPermissions(AccessRoles.ADMIN, user.role as PermissionRoles);

    return (
        <header border-radius={borderRadius} className="table_header">
            <div className="table_header__container">
                <div className="table_header__wrapper">
                    <div className="table_header__information">
                        <div className="header">
                            <h3 className="header__title">{title}</h3>

                            {isAccessPermission && onAddRowHandler && (
                                <button
                                    type="button"
                                    className="add_button"
                                    onClick={onAddRowHandler}
                                    disabled={uploadAction}
                                >
                                    <img src={add} alt={`Add new ${title}`} />
                                </button>
                            )}

                            {headerActions}

                            {isAccessPermission && (
                                <>
                                    {onUploadFileHandler && (
                                        <DownloadButton
                                            onChange={onUploadFileHandler}
                                            action="upload"
                                            disabled={uploadAction}
                                        />
                                    )}

                                    {onDownloadFileHandler &&
                                    dataTableLength ? (
                                        <DownloadButton
                                            onDownloadClick={
                                                onDownloadFileHandler
                                            }
                                            action="download"
                                            disabled={uploadAction}
                                        />
                                    ) : null}

                                    {onUpdateFileHandler && (
                                        <DownloadButton
                                            onChange={onUpdateFileHandler}
                                            action="update"
                                            disabled={uploadAction}
                                        />
                                    )}
                                </>
                            )}
                        </div>

                        {dataTableLength ? (
                            <h2 className="header__subtitle">
                                {pageSize} items in 1 page
                            </h2>
                        ) : null}
                    </div>
                    {children}
                </div>
            </div>
        </header>
    );
};

export default TableHeader;

interface TableHeaderRightProps {
    children: ReactNode;
}

export const TableHeaderRight: FC<TableHeaderRightProps> = ({ children }) => {
    return <div className="header__right">{children}</div>;
};
